import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  useTable,
  usePagination,
  useBlockLayout,
  useResizeColumns,
  useSortBy,
} from "react-table";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "../../../axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import styles from "./Index.module.css";

import Button from "../../../components/Button/Button";
import Spinner from "../../../components/Spinner/Spinner";
import SearchBox from "../../../components/SearchBox/SearchBox";
import EditButton from "../../../components/EditButton/EditButton";
import DeleteButton from "../../../components/DeleteButton/DeleteButton";
import Modal from "../../../components/Modal/Modal";
import FormikControl from "../../../components/FormikControl/FormikControl";
import DialogBox from "../../../components/DialogBox/DialogBox";

import AddNewIcon from "../../../assets/images/add-new-icon.svg";
import PrevIcon from "../../../assets/images/left-arrow-icon.svg";
import FirstIcon from "../../../assets/images/double-left-arrow-icon.svg";
import NextIcon from "../../../assets/images/right-arrow-icon.svg";
import LastIcon from "../../../assets/images/double-right-arrow-icon.svg";

const Table = ({
  columns,
  data,
  onSort,
  fetchData,
  pageCount: controlledPageCount,
  recordCount,
  setSearchKeyword,
}) => {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize, sortBy },
  } = useTable(
    {
      columns,
      data,
      initialState: {
        pageIndex: 0,
        sortBy: [
          {
            id: "bank_name",
            desc: false,
          },
        ],
      },
      manualPagination: true,
      manualSortBy: true,
      autoResetPage: false,
      autoResetSortBy: false,
      pageCount: controlledPageCount,
    },
    useSortBy,
    usePagination,
    useBlockLayout,
    useResizeColumns
  );

  useEffect(() => {
    onSort({ sortBy, pageIndex, pageSize });
    fetchData({ pageIndex, pageSize, sortBy });
  }, [fetchData, pageIndex, pageSize, onSort, sortBy]);

  const searchTable = (event) => {
    if (event.key === "Enter") {
      setSearchKeyword(event.target.value);
    }
  };

  return (
    <>
      <div className="TableHeader">
        <div className="SearchTable">
          <SearchBox
            placeholder="Search by Name"
            onKeyPress={(event) => searchTable(event)}
          />
        </div>
      </div>
      <table {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th {...column.getHeaderProps(column.getSortByToggleProps())}>
                  {column.render("Header")}
                  <span>
                    {column.isSorted
                      ? column.isSortedDesc
                        ? " 🔽"
                        : " 🔼"
                      : ""}
                  </span>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        {recordCount > 0 ? (
          <tbody {...getTableBodyProps()}>
            {page.map((row, i) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => {
                    return (
                      <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        ) : (
          <tbody>
            <tr>
              <td colSpan="10000">No records found!</td>
            </tr>
          </tbody>
        )}
      </table>
      <div className="TableFooter">
        <div className="TableRecordsCount">
          <div className="PageSize">
            <label>Show</label>
            <select
              className="FormControl"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
              }}
            >
              {[10, 20, 30, 40, 50, 100, 500].map((pageSize) => (
                <option key={pageSize} value={pageSize}>
                  {pageSize}
                </option>
              ))}
            </select>
          </div>
          <div className="RecordsStartEnd">
            Showing {page.length} of {recordCount}
          </div>
        </div>
        <div className="TablePagination">
          <div className="PageCountStat">
            Page
            <span>
              {pageIndex + 1} of {pageCount}
            </span>
          </div>
          <div>|</div>
          <div className="GoToPage">
            <label>Go to page</label>
            <input
              className="FormControl"
              type="number"
              defaultValue={pageIndex + 1}
              onChange={(e) => {
                const page = e.target.value ? Number(e.target.value) - 1 : 0;
                gotoPage(page);
              }}
            />
          </div>
          <div className="PaginationNav">
            <Button
              type="button"
              btnType="Default"
              clicked={() => gotoPage(0)}
              disabled={!canPreviousPage}
            >
              <img src={FirstIcon} alt="First Page" />
            </Button>
            <Button
              type="button"
              btnType="Default"
              clicked={() => previousPage()}
              disabled={!canPreviousPage}
            >
              <img src={PrevIcon} alt="Previous Page" />
            </Button>
            <Button
              type="button"
              btnType="Default"
              clicked={() => nextPage()}
              disabled={!canNextPage}
            >
              <img src={NextIcon} alt="Next Page" />
            </Button>
            <Button
              type="button"
              btnType="Default"
              clicked={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
            >
              <img src={LastIcon} alt="Last Page" />
            </Button>
          </div>
        </div>
      </div>
    </>
  );
};

const Index = (props) => {
  const columns = React.useMemo(
    () => [
      {
        Header: "Bank",
        accessor: "bank_name",
        width: 450,
      },
      {
        Header: "Account No",
        accessor: "account_no",
        width: 400,
      },
      {
        Header: "IFSC",
        accessor: "ifsc",
        width: 200,
      },
      {
        Header: "",
        accessor: "id",
        Cell: ({ cell }) => [
          <EditButton clicked={() => handleEditForm(cell.row.values.id)} />,
          <DeleteButton clicked={() => handleDelete(cell.row.values.id)} />,
        ],
        disableSortBy: true,
        width: 200,
      },
    ],
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // We'll start our table without any data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [recordCount, setRecordCount] = useState(0);
  const [startRecord, setStartRecord] = useState(0);
  const [endRecord, setEndRecord] = useState(0);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [showDeleteDialogBox, setShowDeleteDialogBox] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const fetchIdRef = useRef(0);

  const loadData = useCallback(
    (pageSize, pageIndex, sortBy) => {
      let orderBy = {
        order: "bank_name",
        by: "ASC",
      };

      if (sortBy.length) {
        orderBy = {
          order: sortBy[0].id,
          by: sortBy[0].desc ? "DESC" : "ASC",
        };
      }

      axios
        .get(
          "/users/" +
            props.userId +
            "/banks?keyword=" +
            searchKeyword +
            "&per-page=" +
            pageSize +
            "&page=" +
            (pageIndex + 1) +
            "&order=" +
            orderBy.order +
            "&by=" +
            orderBy.by
        )
        .then((response) => {
          setData(response.data);
          setPageCount(response.headers["x-pagination-page-count"]);
          setRecordCount(response.headers["x-pagination-total-count"]);
          setStartRecord(response.headers["x-pagination-start-record"]);
          setEndRecord(response.headers["x-pagination-end-record"]);
          setLoading(false);
        })
        .catch((error) => {
          setLoading(false);
        });
    },
    [props.userId, searchKeyword]
  );

  const [pageDetails, setPageDetails] = useState(null);

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sortBy }) => {
      const fetchId = ++fetchIdRef.current;

      setLoading(true);

      if (fetchId === fetchIdRef.current) {
        loadData(pageSize, pageIndex, sortBy);
      }

      setPageDetails({
        pageIndex: pageIndex,
        pageSize: pageSize,
        sortBy: sortBy,
      });
    },
    [loadData]
  );

  const handleSort = useCallback(() => {
    setLoading(true);
  }, []);

  const [formValues, setFormValues] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showNewForm, setShowNewForm] = useState(false);

  const handleNewForm = () => {
    const formValues = {
      bank_name: "",
      account_no: "",
      ifsc: "",
    };

    setFormValues(formValues);
    setEditMode(false);
    setShowNewForm(!showNewForm);
  };

  const handleEditForm = (id) => {
    axios
      .get(`/users/${props.userId}/banks/${id}`)
      .then((response) => {
        const formValues = {
          id: response.data.id,
          bank_name: response.data.bank_name,
          account_no: response.data.account_no,
          ifsc: response.data.ifsc,
        };

        setFormValues(formValues);
        setEditMode(true);
        setShowNewForm(true);
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  };

  const initialValues = {
    bank_name: formValues ? formValues.bank_name : "",
    account_no: formValues ? formValues.account_no : "",
    ifsc: formValues ? formValues.ifsc : "",
  };

  const validationSchema = Yup.object({
    bank_name: Yup.string().required("Bank Name is required"),
    account_no: Yup.string()
      .required("Account No is required")
      .matches(/^[0-9.]+$/, "Only numbers are allowed"),
    ifsc: Yup.string()
      .required("IFSC is required")
      .trim()
      .min(11, "IFSC must be at least 11 characters")
      .max(11, "IFSC not be more than 11 characters")
      .matches(/^[A-Z0-9]+$/, "Not a valid IFSC"),
  });

  const onSubmit = (values) => {
    setLoading(true);

    const formData = {
      bank_name: values.bank_name,
      account_no: values.account_no,
      ifsc: values.ifsc,
    };

    if (!editMode) {
      axios
        .post(`/users/${props.userId}/banks`, formData)
        .then((response) => {
          toast.success(response.data.message);
          loadData(
            pageDetails.pageSize,
            pageDetails.pageIndex,
            pageDetails.sortBy
          );
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      axios
        .put(`/users/${props.userId}/banks/${formValues.id}`, formData)
        .then((response) => {
          toast.success(response.data.message);
          loadData(
            pageDetails.pageSize,
            pageDetails.pageIndex,
            pageDetails.sortBy
          );
          setShowNewForm(false);
        })
        .catch((error) => {
          if (error.response.data.code === 2001) {
            const errors = error.response.data.errors;
            toast.error(Object.values(errors[0])[0]);
          } else {
            toast.error(error.response.data.message);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const newForm = showNewForm ? (
    <Modal
      show={showNewForm}
      modalClosed={handleNewForm}
      title={`${editMode ? "EDIT" : "ADD"}
        Bank`}
    >
      <div className="FormContainer ModalForm">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
        >
          {() => {
            return (
              <Form>
                <div className={["FormBody", styles.FormBody].join(" ")}>
                  <FormikControl
                    control="input"
                    type="text"
                    label="Bank Name *"
                    name="bank_name"
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="Account No *"
                    name="account_no"
                  />
                  <FormikControl
                    control="input"
                    type="text"
                    label="IFSC *"
                    name="ifsc"
                    maxLength="11"
                  />
                </div>
                <div className="FormActions">
                  <Button btnType="Primary" type="Submit">
                    Save
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    </Modal>
  ) : null;

  const handleDelete = (id = null) => {
    setShowDeleteDialogBox(!showDeleteDialogBox);
    setActiveItem(id);
  };

  const confirmDelete = () => {
    axios
      .delete(`/users/${props.userId}/banks/${activeItem}`)
      .then((response) => {
        toast.success(response.data.message);
        setShowDeleteDialogBox(false);
        loadData(
          pageDetails.pageSize,
          pageDetails.pageIndex,
          pageDetails.sortBy
        );
      })
      .catch((error) => {
        if (error.response.data.code === 2001) {
          const errors = error.response.data.errors;
          toast.error(Object.values(errors[0])[0]);
        } else {
          toast.error(error.response.data.message);
        }
      });
  };

  return (
    <React.Fragment>
      <div className="DataTable FixedWidth">
        <div className={styles.TableActions}>
          <Button btnType="Success" clicked={handleNewForm}>
            <img src={AddNewIcon} alt="New" /> <span>New</span>
          </Button>
        </div>

        <Table
          columns={columns}
          data={data}
          onSort={handleSort}
          fetchData={fetchData}
          loading={loading}
          pageCount={pageCount}
          recordCount={recordCount}
          startRecord={startRecord}
          endRecord={endRecord}
          setSearchKeyword={setSearchKeyword}
        />
      </div>
      <DialogBox
        show={showDeleteDialogBox}
        confirm={confirmDelete}
        cancel={handleDelete}
        title="Delete Bank Account"
        message="Are you sure you want to delete this Bank Account?"
      />
      <Spinner isLoading={loading} />
      {newForm}
      <ToastContainer />
    </React.Fragment>
  );
};

export default Index;
